import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Search, Heart, User, Plus, ChevronDown, MoreVertical,
  History, Globe, FlaskConical, Users as UsersIcon, Palette,
  Calculator, Languages, Cpu, MoreHorizontal, BookOpen
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { useLike } from '../context/LikeContext'
import RoomEntryModal from '../components/RoomEntryModal'

/* === Styled === */
const Page = styled.div`
  padding: 28px 32px 60px;
  background: ${p => p.theme.backgroundColor};
  min-height: calc(100vh - 64px);
  box-sizing: border-box;

  @media (max-width: 768px) { padding: 20px 16px 50px; }
`

const HelloBanner = styled.div`
  position: relative;
  border-radius: 20px;
  margin-bottom: 0;
  overflow: hidden;
  height: 180px;
  background: #f5f3ff;
  box-shadow: 0 12px 30px rgba(124, 58, 237, 0.10), 0 2px 6px rgba(0,0,0,0.04);
  border: 1px solid rgba(196, 181, 253, 0.30);

  img.bg {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: right center;
  }

  .text {
    position: absolute;
    top: 50%;
    left: 36px;
    transform: translateY(-50%);
    z-index: 2;
    max-width: 50%;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    gap: 8px;

    h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.5px;
      line-height: 1.25;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    p {
      margin: 0;
      font-size: 14px;
      color: #475569;
      line-height: 1.4;
    }
  }

  @media (max-width: 768px) {
    height: 150px;
    .text {
      left: 24px;
      max-width: 55%;
      h2 { font-size: 19px; }
      p { font-size: 12.5px; }
    }
  }

  @media (max-width: 480px) {
    height: 130px;
    .text {
      left: 18px;
      max-width: 60%;
      h2 { font-size: 16px; }
      p { font-size: 11.5px; }
    }
  }
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 18px;
  margin-bottom: 22px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const TopRightCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 900px) {
    flex-direction: row;
    justify-content: flex-end;
  }
`

const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
`

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${p => p.$active ? 'transparent' : p.theme.border};
  background: ${p => p.$active ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : p.theme.cardBackground};
  color: ${p => p.$active ? '#ffffff' : p.theme.textSecondary};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  box-shadow: ${p => p.$active ? '0 4px 12px rgba(124,58,237,0.25)' : 'none'};

  svg { width: 14px; height: 14px; }

  &:hover {
    border-color: #7c3aed;
    color: ${p => p.$active ? '#ffffff' : '#7c3aed'};
  }
`

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const SortSelect = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${p => p.theme.cardBackground};
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;
  font-size: 12.5px;
  color: ${p => p.theme.textPrimary};
  cursor: pointer;
  font-weight: 600;

  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const CreateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(124,58,237,0.30);
  transition: all 0.15s;

  &:hover { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(124,58,237,0.40); }
`

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 18px 0 12px;

  h3 { margin: 0; font-size: 16px; font-weight: 800; color: ${p => p.theme.textPrimary}; }
  .count {
    background: rgba(124,58,237,0.12);
    color: #7c3aed;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 700;
  }
  .spacer { flex: 1; }
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
`

const SetCard = styled.div`
  background: ${p => p.theme.cardBackground};
  border: 1px solid ${p => p.theme.border};
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.20s ease;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 18px 36px rgba(124, 58, 237, 0.15), 0 4px 12px rgba(0,0,0,0.06);
    border-color: #c4b5fd;
  }
`

const CardImage = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  background: ${p => p.$image ? `url(${p.$image}) center/cover` : 'linear-gradient(135deg, #c4b5fd, #93c5fd)'};
`

const CategoryBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  color: #ffffff;
  background: ${p => p.$bg || '#7c3aed'};
  letter-spacing: 0.2px;
`

const CardMenuBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: rgba(255,255,255,0.95);
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.10);
  &:hover { background: #ffffff; color: #0f172a; }
`

const CardBody = styled.div`
  padding: 14px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const CardTitle = styled.h4`
  margin: 0;
  font-size: 14.5px;
  font-weight: 700;
  color: ${p => p.theme.textPrimary};
  line-height: 1.3;
  letter-spacing: -0.2px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CardDesc = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${p => p.theme.textSecondary};
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 33px;
`

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  padding-top: 10px;
  border-top: 1px solid ${p => p.theme.borderLight};
  font-size: 11.5px;
  color: ${p => p.theme.textMuted};

  .left {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    transition: color 0.15s;
    &:hover { color: #ef4444; }
    &.liked { color: #ef4444; }
  }
  .right {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
`

const LoadMoreWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 28px;
`

const LoadMoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: ${p => p.theme.cardBackground};
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.textPrimary};
  cursor: pointer;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const EmptyState = styled.div`
  background: ${p => p.theme.cardBackground};
  border: 1px solid ${p => p.theme.border};
  border-radius: 14px;
  padding: 60px 30px;
  text-align: center;
  color: ${p => p.theme.textSecondary};
`

/* === 우측 사이드바 (Step 4) === */
const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 20px;
  align-items: start;

  @media (max-width: 1280px) {
    grid-template-columns: 1fr;
  }
`

const RightSide = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 80px;

  @media (max-width: 1280px) { display: none; }
`

const SidePanel = styled.div`
  background: ${p => p.theme.cardBackground};
  border: 1px solid ${p => p.theme.border};
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
`

const SidePanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h4 { margin: 0; font-size: 14px; font-weight: 800; color: ${p => p.theme.textPrimary}; letter-spacing: -0.2px; }
  .more {
    font-size: 11.5px;
    color: ${p => p.theme.textMuted};
    background: ${p => p.theme.hoverBackground};
    border: 1px solid ${p => p.theme.border};
    border-radius: 6px;
    padding: 3px 9px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    cursor: pointer;
    font-weight: 600;
    &:hover { color: #7c3aed; border-color: #c4b5fd; }
  }
`

const RecentItem = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid ${p => p.theme.borderLight};

  &:last-child { border-bottom: none; padding-bottom: 0; }
  &:first-child { padding-top: 0; }

  .icon {
    width: 30px; height: 30px;
    border-radius: 8px;
    background: ${p => p.$bg || 'rgba(124,58,237,0.12)'};
    color: ${p => p.$color || '#7c3aed'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .body {
    flex: 1;
    min-width: 0;
    .title {
      font-size: 12px;
      color: ${p => p.theme.textPrimary};
      font-weight: 600;
      line-height: 1.45;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .time {
      font-size: 10.5px;
      color: ${p => p.theme.textMuted};
      margin-top: 2px;
    }
  }
`

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding: 4px 0 6px;

  .item {
    text-align: center;
    .num { display: block; font-size: 22px; font-weight: 800; color: ${p => p.theme.textPrimary}; line-height: 1.1; }
    .lbl { display: block; font-size: 10.5px; color: ${p => p.theme.textMuted}; margin-top: 4px; }
  }
`

const ChartBox = styled.div`
  margin-top: 8px;

  svg { width: 100%; height: 80px; display: block; }

  .x-labels {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-top: 4px;
    font-size: 9.5px;
    color: ${p => p.theme.textMuted};
    text-align: center;
  }
`

const RecentActivityIcon = ({ type }) => {
  // 아이콘은 BookOpen으로 통일
  const map = {
    created: { bg: 'rgba(124,58,237,0.12)', color: '#7c3aed' },
    edited: { bg: 'rgba(245,158,11,0.14)', color: '#d97706' },
    added: { bg: 'rgba(16,185,129,0.14)', color: '#10b981' },
    shared: { bg: 'rgba(59,130,246,0.14)', color: '#2563eb' },
  }
  const v = map[type] || map.created
  return v
}

/* Login modal (legacy) */
const LoginRequiredModal = styled.div`
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
`

const ModalContent = styled.div`
  background: ${p => p.theme.cardBackground};
  border-radius: 14px;
  padding: 30px;
  text-align: center;
  width: min(90%, 380px);
`

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.theme.textPrimary};
  margin: 0 0 12px;
`

const ModalMessage = styled.p`
  font-size: 14px;
  color: ${p => p.theme.textSecondary};
  line-height: 1.5;
  margin: 0 0 22px;
`

const ModalButton = styled.button`
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  padding: 10px 22px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  margin: 0 4px;
`

const ModalSecondaryButton = styled.button`
  background: ${p => p.theme.cardBackground};
  color: ${p => p.theme.textSecondary};
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;
  padding: 10px 22px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  margin: 0 4px;
`

const RoomEntryButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 14px 22px;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  color: #ffffff;
  border: none;
  border-radius: 999px;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(124,58,237,0.40);
  z-index: 100;
  &:hover { transform: translateY(-2px); }
`

/* Categories (icon + color) — backend 미연동 시 클라이언트 placeholder */
const CATEGORIES = [
  { key: 'all', label: '전체', icon: BookOpen, color: '#7c3aed' },
  { key: '역사', label: '역사', icon: History, color: '#a855f7' },
  { key: '과학', label: '과학', icon: FlaskConical, color: '#10b981' },
  { key: '사회', label: '사회', icon: UsersIcon, color: '#3b82f6' },
  { key: '예술', label: '예술', icon: Palette, color: '#ec4899' },
  { key: '수학', label: '수학', icon: Calculator, color: '#f59e0b' },
  { key: '언어', label: '언어', icon: Languages, color: '#14b8a6' },
  { key: 'IT/기술', label: 'IT/기술', icon: Cpu, color: '#6366f1' },
]

const formatDate = (s) => {
  if (!s) return ''
  const d = new Date(s)
  if (isNaN(d.getTime())) return ''
  const yy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yy}.${mm}.${dd}`
}

export default function HomePage() {
  const { theme } = useTheme()
  const { isLoggedIn, user, loading: authLoading } = useAuth()
  const { initializeLikeStates, toggleLike, isLiked } = useLike()
  const navigate = useNavigate()
  const location = useLocation()

  const [searchTerm, setSearchTerm] = useState('')
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [likeCounts, setLikeCounts] = useState({})
  const [quizzes, setQuizzes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showRoomModal, setShowRoomModal] = useState(false)
  const [activeCategory, setActiveCategory] = useState('all')
  const [visibleCount, setVisibleCount] = useState(12)

  // URL의 검색어 적용
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const q = params.get('q')
    if (q) setSearchTerm(q)
  }, [location.search])

  const fetchPublicQuizzes = async () => {
    try {
      setLoading(true)
      setError(null)
      const timestamp = new Date().getTime()
      const url = `/auth/get-public-quizzes?limit=50&t=${timestamp}`
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-cache',
        headers: { 'Cache-Control': 'no-cache' },
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      const data = await response.json()
      if (data.success) {
        const formattedQuizzes = data.quizzes.map(quiz => ({
          id: quiz.set_id,
          title: quiz.set_title,
          image: quiz.set_cover_img || '/ThumbnailEx1.jpg',
          likes: parseInt(quiz.set_like_count) || 0,
          author: quiz.set_author,
          plays: parseInt(quiz.set_play_count) || 0,
          description: quiz.set_description,
          category: quiz.set_category || quiz.category || null,
          createdAt: quiz.created_at || quiz.set_created_at || null,
        }))
        setQuizzes(formattedQuizzes)

        // 좋아요 상태 로드
        try {
          const likeResponse = await fetch('/auth/get-liked-quizzes', {
            method: 'GET',
            credentials: 'include',
          })
          const likeData = await likeResponse.json()
          if (likeData.success && likeData.likedQuizzes) {
            const formattedLikedSets = likeData.likedQuizzes.map(quiz => ({
              id: parseInt(quiz.set_id),
            }))
            initializeLikeStates(formattedLikedSets)
          }
        } catch (likeError) {
          console.log('❤️ 좋아요 상태 로드 오류:', likeError)
        }
      } else {
        setError('퀴즈 목록을 불러오는데 실패했습니다.')
      }
    } catch (e) {
      console.error('💥 공개 퀴즈 목록 조회 실패:', e)
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPublicQuizzes()
  }, [])

  useEffect(() => {
    if (location.state?.newQuizCreated) {
      setTimeout(() => fetchPublicQuizzes(), 1000)
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  const filteredSets = quizzes
    .filter(set => {
      if (activeCategory === 'all') return true
      return (set.category || '').includes(activeCategory)
    })
    .filter(set =>
      (set.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (set.author || '').toLowerCase().includes(searchTerm.toLowerCase())
    )

  const visibleSets = filteredSets.slice(0, visibleCount)
  const canLoadMore = visibleSets.length < filteredSets.length

  const handleHeartClick = async (setId) => {
    if (authLoading) return
    if (!isLoggedIn) {
      setShowLoginModal(true)
      return
    }
    const result = await toggleLike(setId)
    if (result.success) {
      setLikeCounts(prev => ({
        ...prev,
        [setId]: (prev[setId] !== undefined ? prev[setId] : quizzes.find(q => q.id === setId)?.likes || 0) + (result.isLiked ? 1 : -1),
      }))
    } else {
      alert(result.error)
    }
  }

  const handleLoginRedirect = () => {
    setShowLoginModal(false)
    navigate('/login?from=/')
  }

  const getLikeCount = (setId, originalLikes) =>
    likeCounts[setId] !== undefined ? likeCounts[setId] : originalLikes

  const getCategoryColor = (cat) => {
    const c = CATEGORIES.find(x => cat && cat.includes(x.key))
    return c ? c.color : '#7c3aed'
  }

  const userName = user?.user_name || user?.username || user?.name || ''

  return (
    <Page theme={theme}>
      <Layout>
        <div>
        {isLoggedIn && userName && (
          <HelloBanner style={{ marginBottom: 22 }}>
            <img src="/home-banner.png" alt="" className="bg" />
            <div className="text">
              <h2>👋 안녕하세요, {userName}님!</h2>
              <p>오늘도 새로운 문제 세트를 만들어보세요.</p>
            </div>
          </HelloBanner>
        )}
        <HeaderRow>
          <ChipRow>
            {CATEGORIES.map(c => {
              const Icon = c.icon
              return (
                <Chip
                  key={c.key}
                  theme={theme}
                  $active={activeCategory === c.key}
                  onClick={() => { setActiveCategory(c.key); setVisibleCount(12) }}
                >
                  <Icon /> {c.label}
                </Chip>
              )
            })}
            <Chip theme={theme} onClick={() => alert('카테고리 더보기 준비 중')}>
              <MoreHorizontal /> 더보기
            </Chip>
          </ChipRow>
        </HeaderRow>

      <SectionHead theme={theme}>
        <h3>{activeCategory === 'all' ? '전체 문제 세트' : `${activeCategory} 문제 세트`}</h3>
        <span className="count">{filteredSets.length}</span>
        <span className="spacer" />
        <SortSelect theme={theme}>
          정렬: 최신순 <ChevronDown size={14} />
        </SortSelect>
      </SectionHead>

      {loading ? (
        <EmptyState theme={theme}>퀴즈 목록을 불러오는 중...</EmptyState>
      ) : error ? (
        <EmptyState theme={theme}>
          <h3 style={{ margin: '0 0 8px', color: theme.textPrimary }}>오류가 발생했습니다</h3>
          <p style={{ margin: '0 0 14px' }}>{error}</p>
          <ModalButton onClick={fetchPublicQuizzes}>다시 시도</ModalButton>
        </EmptyState>
      ) : filteredSets.length === 0 ? (
        <EmptyState theme={theme}>
          {searchTerm
            ? `"${searchTerm}"에 대한 검색 결과가 없습니다.`
            : '아직 공개된 퀴즈가 없습니다.'}
        </EmptyState>
      ) : (
        <>
          <CardGrid>
            {visibleSets.map(set => (
              <SetCard
                key={set.id}
                theme={theme}
                onClick={() => navigate(`/problem/${set.id}`)}
              >
                <CardImage $image={set.image}>
                  {set.category && (
                    <CategoryBadge $bg={getCategoryColor(set.category)}>
                      {set.category}
                    </CategoryBadge>
                  )}
                  <CardMenuBtn
                    onClick={(e) => {
                      e.stopPropagation()
                      alert('카드 메뉴 준비 중')
                    }}
                  >
                    <MoreVertical size={14} />
                  </CardMenuBtn>
                </CardImage>
                <CardBody>
                  <CardTitle theme={theme}>{set.title}</CardTitle>
                  <CardDesc theme={theme}>
                    {set.description || ' '}
                  </CardDesc>
                  <CardMeta theme={theme}>
                    <span
                      className={`left ${isLiked(set.id) ? 'liked' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleHeartClick(set.id)
                      }}
                    >
                      <Heart size={12} fill={isLiked(set.id) ? '#ef4444' : 'none'} />
                      {getLikeCount(set.id, set.likes)}
                    </span>
                    <span className="right">
                      {set.createdAt ? formatDate(set.createdAt) : (
                        <>
                          <User size={11} /> {set.author}
                        </>
                      )}
                    </span>
                  </CardMeta>
                </CardBody>
              </SetCard>
            ))}
          </CardGrid>

          {canLoadMore && (
            <LoadMoreWrap>
              <LoadMoreBtn theme={theme} onClick={() => setVisibleCount(v => v + 12)}>
                더 많은 문제 세트 보기 <ChevronDown size={14} />
              </LoadMoreBtn>
            </LoadMoreWrap>
          )}
        </>
      )}
        </div>

        <RightSide>
          {isLoggedIn && (
            <CreateBtn
              onClick={() => navigate('/create')}
              style={{
                height: 180,
                flexDirection: 'column',
                gap: 10,
                fontSize: 15,
                borderRadius: 20,
                padding: 20,
                justifyContent: 'center',
              }}
            >
              <Plus size={32} strokeWidth={2.5} />
              문제 세트 만들기
            </CreateBtn>
          )}
          <SidePanel theme={theme}>
            <SidePanelHead theme={theme}>
              <h4>최근 활동</h4>
            </SidePanelHead>
            {[
              { type: 'created', title: '새 문제 세트를 생성했어요', time: '2시간 전' },
              { type: 'edited',  title: '문제 세트를 수정했어요',   time: '1일 전' },
              { type: 'added',   title: '새 문제를 추가했어요',     time: '2일 전' },
              { type: 'shared',  title: '문제 세트를 공유했어요',   time: '3일 전' },
            ].map((act, i) => {
              const v = RecentActivityIcon({ type: act.type })
              return (
                <RecentItem key={i} theme={theme} $bg={v.bg} $color={v.color}>
                  <div className="icon">
                    <BookOpen size={14} />
                  </div>
                  <div className="body">
                    <div className="title">{act.title}</div>
                    <div className="time">{act.time}</div>
                  </div>
                </RecentItem>
              )
            })}
          </SidePanel>

          <SidePanel theme={theme}>
            <SidePanelHead theme={theme}>
              <h4>주간 통계</h4>
              <span className="more">이번 주 <ChevronDown size={11} /></span>
            </SidePanelHead>
            <StatRow theme={theme}>
              <div className="item">
                <span className="num">{quizzes.length}</span>
                <span className="lbl">생성한 문제</span>
              </div>
              <div className="item">
                <span className="num">{quizzes.reduce((acc, q) => acc + (q.plays || 0), 0)}</span>
                <span className="lbl">푼 문제</span>
              </div>
              <div className="item">
                <span className="num">{quizzes.reduce((acc, q) => acc + (q.likes || 0), 0)}</span>
                <span className="lbl">참여자</span>
              </div>
            </StatRow>

            <ChartBox theme={theme}>
              {(() => {
                const data = [10, 14, 8, 18, 16, 22, 19]
                const max = Math.max(...data, 1)
                const points = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - (v / max) * 90}`).join(' ')
                return (
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.30" />
                        <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <polyline points={points} fill="none" stroke="#7c3aed" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                    <polygon points={`0,100 ${points} 100,100`} fill="url(#chartGrad)" />
                    {data.map((v, i) => (
                      <circle
                        key={i}
                        cx={(i / (data.length - 1)) * 100}
                        cy={100 - (v / max) * 90}
                        r="1.5"
                        fill="#ffffff"
                        stroke="#7c3aed"
                        strokeWidth="1.2"
                        vectorEffect="non-scaling-stroke"
                      />
                    ))}
                  </svg>
                )
              })()}
              <div className="x-labels">
                {(() => {
                  const today = new Date()
                  const days = []
                  for (let i = 6; i >= 0; i--) {
                    const d = new Date(today)
                    d.setDate(today.getDate() - i)
                    days.push(`${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`)
                  }
                  return days.map(d => <span key={d}>{d}</span>)
                })()}
              </div>
            </ChartBox>
          </SidePanel>
        </RightSide>
      </Layout>

      <RoomEntryButton onClick={() => setShowRoomModal(true)}>
        🎮 방 번호로 참가
      </RoomEntryButton>

      {showLoginModal && (
        <LoginRequiredModal onClick={() => setShowLoginModal(false)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModalTitle theme={theme}>로그인이 필요한 서비스입니다</ModalTitle>
            <ModalMessage theme={theme}>
              좋아요 기능을 사용하려면 로그인이 필요합니다.<br />
              로그인하시겠습니까?
            </ModalMessage>
            <div>
              <ModalButton onClick={handleLoginRedirect}>로그인</ModalButton>
              <ModalSecondaryButton theme={theme} onClick={() => setShowLoginModal(false)}>
                취소
              </ModalSecondaryButton>
            </div>
          </ModalContent>
        </LoginRequiredModal>
      )}

      <RoomEntryModal
        isOpen={showRoomModal}
        onClose={() => setShowRoomModal(false)}
      />
    </Page>
  )
}
